import React from "react"
import { FormProvider } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { DnDProvider } from "shared/context/drag-and-drop/DnDProvider"
import { useEvent } from "shared/lib/useEvent"
import { Button } from "shared/ui/buttons/Button"
import { TextInput } from "shared/ui/input/text/TextInput"
import { ProductInput } from "shared/ui/product-input/ProductInput"
import { Skeleton } from "shared/ui/skeleton/Skeleton"

import { ProductSelectOption } from "entities/products/types/ProductSelectOption"

import { useFollowUpConfigurationAdminPageViewModel } from "./model/useFollowUpConfigurationAdminPageViewModel"
import "./styles/_follow-up-config-admin-page.scss"
import { FollowUpConfigurationGroupList } from "./ui/FollowUpConfigurationGroupList"

type ViewModel = ReturnType<typeof useFollowUpConfigurationAdminPageViewModel>

export interface FollowUpConfigurationAdminPageViewProps extends ViewModel {
  productOptions: ProductSelectOption[]
  isLoading: boolean
  error?: Error
  isCreating: boolean
  isUpdating: boolean
  isDeleting: boolean
  hasConfigurations: boolean
}

export function FollowUpConfigurationAdminPageView({
  groups,
  filterText,
  setFilterText,
  isReorderDisabled,
  productOptions,
  newEntryForm,
  handleUpdateRow,
  handleDelete,
  handleAddToGroup,
  handleAddEntry,
  isLoading,
  error,
  isCreating,
  isUpdating,
  isDeleting,
  hasConfigurations
}: FollowUpConfigurationAdminPageViewProps) {
  const { t } = useTranslation()

  const handleFilterChange = useEvent(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setFilterText(event.target.value)
  )

  if (isLoading) {
    return (
      <div className="follow-up-config-admin-page">
        <Skeleton count={6} height={72} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="follow-up-config-admin-page">
        <div className="follow-up-config-admin-page__error">
          {t("General.Error")}: {error.message}
        </div>
      </div>
    )
  }

  return (
    <div className="follow-up-config-admin-page">
      <div className="follow-up-config-admin-page__header">
        <h1 className="follow-up-config-admin-page__title">
          {t("FollowUpConfiguration.Admin.Title")}
        </h1>
        <p className="follow-up-config-admin-page__description">
          {t("FollowUpConfiguration.Admin.Description")}
        </p>
      </div>

      <TextInput.Root
        label={t("FollowUpConfiguration.Admin.Filter")}
        className="follow-up-config-admin-page__filter"
      >
        <TextInput.Label />
        <TextInput.Focus>
          <TextInput.Input
            type="search"
            value={filterText}
            onChange={handleFilterChange}
            placeholder={t("FollowUpConfiguration.Placeholders.Filter")}
          />
        </TextInput.Focus>
      </TextInput.Root>

      {isReorderDisabled ? (
        <p className="follow-up-config-admin-page__filter-hint">
          {t("FollowUpConfiguration.Hints.ReorderDisabledWhileFiltering")}
        </p>
      ) : null}

      {groups.length ? (
        <DnDProvider>
          <FollowUpConfigurationGroupList
            groups={groups}
            productOptions={productOptions}
            onUpdate={handleUpdateRow}
            onDelete={handleDelete}
            onAddToGroup={handleAddToGroup}
            isCreating={isCreating}
            isUpdating={isUpdating}
            isDeleting={isDeleting}
            isReorderDisabled={isReorderDisabled}
          />
        </DnDProvider>
      ) : (
        <div className="follow-up-config-admin-page__empty">
          {hasConfigurations
            ? t("FollowUpConfiguration.Admin.NoFilterResults")
            : t("FollowUpConfiguration.Admin.EmptyState")}
        </div>
      )}

      <div className="follow-up-config-admin-page__add card card--elevated">
        <h2 className="follow-up-config-admin-page__add-title">
          {t("FollowUpConfiguration.Admin.AddNew")}
        </h2>
        <FormProvider {...newEntryForm}>
          <form
            className="follow-up-config-admin-page__add-form"
            onSubmit={handleAddEntry}
          >
            <ProductInput
              name="sourceProductName"
              label={t("FollowUpConfiguration.Fields.SourceProduct")}
              placeholder={t(
                "FollowUpConfiguration.Placeholders.SourceProduct"
              )}
              className="follow-up-config-admin-page__add-field"
              options={productOptions}
              multiple={false}
              required
            />

            <ProductInput
              name="targetProductName"
              label={t("FollowUpConfiguration.Fields.TargetProduct")}
              placeholder={t(
                "FollowUpConfiguration.Placeholders.TargetProduct"
              )}
              className="follow-up-config-admin-page__add-field"
              options={productOptions}
              multiple={false}
              required
            />

            <Button
              variant="primary"
              type="submit"
              disabled={isCreating}
              className="follow-up-config-admin-page__add-button"
            >
              {t("FollowUpConfiguration.Admin.AddButton")}
            </Button>
          </form>
        </FormProvider>
      </div>
    </div>
  )
}
